import {useState, useEffect} from 'react';
import makeConstants from 'lib/makeConstants';
import type {QueryBuilder} from '../../albert-io/json-api-framework/request/builder/index';

type Query = QueryBuilder | (() => QueryBuilder);

const queryStatuses = makeConstants('ready', 'fetching', 'notReady');

interface UseAreLatestQueriesReadyOptions {
  /**
   * If true, queries will be awaited one at a time, in the order provided, as
   * opposed to in parallel. This can be useful if a query relies on the result of a previous query.
   */
  resolveInOrder: boolean;
}

interface UseAreLatestQueriesReadyResult {
  isReady: boolean;
  invalidateQueries: () => void;
  errors: (unknown | null)[];
}

/**
 * Hook to ensure you're working with queries that are the most up-to-date.
 * Calling the `invalidte()` function will ensure that `isReady` is `false`
 * until the queries are re-fetched.
 * @example
 * ```js
 *   const {isReady, invalidateQueries} = useAreLatestQueriesReady(
 *     [assignmentQuery, getQuestionSetsQuery],
 *     {
 *       resolveInOrder: true
 *     }
 *   );
 * ```
 * @param queries - Queries that need to resolve before isReady is true. Each query may either be
 *   a QueryBuilder object, or a functions that returns a QueryBuilder object. The latter can be useful if you need
 *   some previously loaded data to build your query.
 * @param options
 * @param options.resolveInOrder - If true, queries will be awaited one at a time, in the orded provided, as
 *   opposed to in parallel. This can be useful if a query relies on the result of a previous query.
 */
export default function useAreLatestQueriesReady(
  queries: Query[],
  options: UseAreLatestQueriesReadyOptions = {resolveInOrder: false}
): UseAreLatestQueriesReadyResult {
  const [status, setStatus] = useState<(typeof queryStatuses)[keyof typeof queryStatuses]>(
    queries.every((query) => getQueryBuilderObject(query).isResourceReady())
      ? queryStatuses.ready
      : queryStatuses.notReady
  );
  const [errors, setErrors] = useState<(unknown | null)[]>(queries.map(() => null));

  useEffect(() => {
    const handleQueries = async () => {
      if (status !== queryStatuses.notReady) {
        return;
      }

      setStatus(queryStatuses.fetching);
      setErrors(queries.map(() => null)); // reset errors

      const newErrors: (unknown | null)[] = [];

      if (options.resolveInOrder) {
        for (const [idx, query] of queries.entries()) {
          try {
             
            await getQueryBuilderObject(query).getResourcePromise();
            newErrors[idx] = null;
          } catch (err) {
            newErrors[idx] = err;
          }
        }
      } else {
        await Promise.all(
          queries.map(async (query, idx) => {
            try {
              await getQueryBuilderObject(query).getResourcePromise();
              newErrors[idx] = null;
            } catch (err) {
              newErrors[idx] = err;
            }
          })
        );
      }
      setErrors(newErrors);
      setStatus(queryStatuses.ready);
    };
    handleQueries();
  });

  const isReady = status === queryStatuses.ready;
  const invalidateQueries = () => {
    queries.forEach((query) => getQueryBuilderObject(query).invalidateInterest());
    setStatus(queryStatuses.notReady);
  };

  return {isReady, invalidateQueries, errors};
}

function getQueryBuilderObject(query: Query): QueryBuilder {
  return typeof query === 'function' ? query() : query;
}
